/**
 * Strength progression: estimated one-rep max, per-exercise history, PRs.
 *
 * Epley's formula (weight x (1 + reps/30)) is used for the estimate. It is an
 * estimate, and it is worst at high reps, so sets over 12 reps are still
 * shown but do not set PRs on their own. Warm-up sets are excluded.
 */
import type { LoggedSet, WorkoutSession } from '../data/schema';
import { round } from './num';

/** Epley estimated 1RM. Returns the weight itself for a single. */
export function estimate1RM(weightKg: number, reps: number): number {
  if (!Number.isFinite(weightKg) || weightKg < 0) throw new RangeError('weight must be zero or greater');
  if (!Number.isInteger(reps) || reps < 1) throw new RangeError('reps must be a positive integer');
  if (reps === 1) return weightKg;
  return round(weightKg * (1 + reps / 30), 3);
}

/** Reps beyond which an estimate is too loose to count as a PR. */
export const PR_MAX_REPS = 12;

export interface SetSummary {
  weightKg: number;
  reps: number;
  e1rm: number;
}

export interface ExerciseSession {
  sessionId: string;
  day: string;
  /** Best set by estimated 1RM. */
  best: SetSummary | null;
  /** Heaviest weight lifted for any reps. */
  topWeightKg: number;
  /** Sum of weight x reps across working sets. */
  volumeKg: number;
  sets: number;
  /** True when `best.e1rm` beats every earlier session's best. */
  pr: boolean;
}

function working(set: LoggedSet): set is LoggedSet & { weightKg: number; reps: number } {
  return !set.warmup && typeof set.weightKg === 'number' && set.weightKg > 0
    && typeof set.reps === 'number' && set.reps > 0;
}

/**
 * History of one exercise across sessions, oldest first, with PRs flagged.
 * A PR is a best-set e1RM strictly above every prior session's best, from a
 * set of at most PR_MAX_REPS reps.
 */
export function exerciseHistory(sessions: WorkoutSession[], exerciseId: string): ExerciseSession[] {
  const rows: ExerciseSession[] = [];
  const ordered = [...sessions].sort((a, b) => a.startedAt.localeCompare(b.startedAt));

  for (const session of ordered) {
    const ex = session.exercises.find((e) => e.exerciseId === exerciseId);
    if (!ex) continue;
    const sets = ex.sets.filter(working);
    if (!sets.length) continue;

    let best: SetSummary | null = null;
    let top = 0;
    let volume = 0;
    for (const s of sets) {
      const e1rm = estimate1RM(s.weightKg, s.reps);
      volume += s.weightKg * s.reps;
      top = Math.max(top, s.weightKg);
      if (!best || e1rm > best.e1rm) best = { weightKg: s.weightKg, reps: s.reps, e1rm };
    }
    rows.push({
      sessionId: session.id, day: session.day, best, topWeightKg: top,
      volumeKg: round(volume, 2), sets: sets.length, pr: false,
    });
  }

  let record = -Infinity;
  for (const row of rows) {
    if (row.best && row.best.reps <= PR_MAX_REPS && row.best.e1rm > record) {
      row.pr = rows.indexOf(row) > 0; // the first session is a baseline, not a PR
      record = row.best.e1rm;
    }
  }
  return rows;
}

/** Every exercise that has ever been logged, with its latest and best e1RM. */
export function exerciseOverview(sessions: WorkoutSession[]): {
  exerciseId: string; sessions: number; lastDay: string; latestE1rm: number | null; bestE1rm: number | null;
}[] {
  const ids = new Set<string>();
  for (const s of sessions) for (const e of s.exercises) ids.add(e.exerciseId);
  const out = [];
  for (const id of ids) {
    const hist = exerciseHistory(sessions, id);
    if (!hist.length) continue;
    const last = hist[hist.length - 1]!;
    const best = hist.reduce<number | null>((acc, h) => (h.best && (acc === null || h.best.e1rm > acc) ? h.best.e1rm : acc), null);
    out.push({ exerciseId: id, sessions: hist.length, lastDay: last.day, latestE1rm: last.best?.e1rm ?? null, bestE1rm: best });
  }
  return out.sort((a, b) => b.lastDay.localeCompare(a.lastDay));
}
